import React from 'react';
import Highcharts from 'highcharts';
import moment from 'moment';
import _ from 'lodash';
import { Button, Modal } from 'react-bootstrap';

import highchartFactory from '../../../util/highcharts';
import { escapeHTML } from '../../../util/text';
import { ANALYSIS_STATUS } from '../../../services/analysis';

import Close from '../../../assets/img/close.svg';

import './XmrChart.scss';

const highchartUtils = highchartFactory(Highcharts);

export const XMR_LABELS = {
  open_link: 'View XmR chart',
  title: {
    ct: 'Cycle Time XmR Chart',
    th: 'Throughput XmR Chart',
    wip: 'WIP XmR Chart',
    wipAge: 'WIP Age XmR Chart',
    ctb: 'Cycle Time per Process Step XmR Chart',
    fe: 'Flow Efficiency XmR Chart'
  },
  y_axis: {
    cycleTimeInMinutes: 'Cycle time in days',
    throughput: 'TASKS_STRING finished per day',
    dailyRate: 'Daily completion rate',
    wip: 'TASKS_STRING in progress per day',
    totalWipAge: 'Total age in days',
    efficiency: 'Flow efficiency in percent'
  },
  x_items: 'TASKS_STRING in finish order',
  x_dates: 'Date',
  process_step: 'Process step',
  average: 'Average',
  upper_limit: 'Upper limit',
  lower_limit: 'Lower limit',
  sigma_lines: '1 and 2 sigma',
  period: 'Points used for limits',
  before_period: 'Earlier points',
  recalculated: 'Limits recalculated',
  signal: 'Signal',
  rule_1_high: 'Above the upper limit',
  rule_1_low: 'Below the lower limit',
  rule_2: '2 of 3 points beyond the same 2 sigma line',
  rule_3: '4 of 5 points beyond the same 1 sigma line',
  rule_4: '8 points on the same side of the average',
  tooltip_item: 'TASK_STRING {n}: {name}',
  tooltip_finished: 'Finished: {date}',
  percent_unit: '{value}%',
  close: 'Close'
};

const ITEM_METRICS = ['cycleTimeInMinutes', 'efficiency'];
const DAYS_METRICS = ['cycleTimeInMinutes', 'totalWipAge'];
const WHOLE_NUMBER_METRICS = ['wip', 'efficiency'];
const HALF_DAY = 12 * 3600 * 1000;
const DATE_FORMAT = 'DD MMM YYYY';
const LIMIT_LINE = { dashStyle: 'Dash', lineWidth: 1.5 };
// Highcharts cannot read SCSS and the app has no shared JS colour module yet (architecture.yml, undefined_areas); kept beside their use like TODO_CFD_COLORS in calculations.js
const XMR_CHART_COLORS = {
  points: '#004080',
  earlier: '#99b7d5',
  line: '#545d66',
  sigma: 'rgba(84, 93, 102, 0.35)',
  signal: '#de7878',
  period: '#eff6fb'
};

const ruleText = signalRule => {
  if (signalRule.rule === 1) {
    return signalRule.status === ANALYSIS_STATUS.HIGH ? XMR_LABELS.rule_1_high : XMR_LABELS.rule_1_low;
  }
  return XMR_LABELS[`rule_${signalRule.rule}`];
};

const valueText = (flowMetric, value) => {
  if (_.includes(DAYS_METRICS, flowMetric)) {
    return value + (value === 1 ? ' day' : ' days');
  }
  if (flowMetric === 'efficiency') {
    return XMR_LABELS.percent_unit.replace('{value}', value);
  }
  if (flowMetric === 'dailyRate') {
    return value.toFixed(2);
  }
  return `${value}`;
};

class XmrChart extends React.Component {
  constructor(props) {
    super(props);
    this.container = React.createRef();
  }

  componentDidUpdate(prevProps) {
    if (this.chart && prevProps.xmr !== this.props.xmr) {
      this.chart.update(this.chartConfig(), true, true);
    }
  }

  componentWillUnmount() {
    this.destroyChart();
  }

  handleEntered = () => {
    highchartUtils.setDefaults();
    this.chart = new Highcharts.Chart(this.container.current, this.chartConfig());
  };

  handleExited = () => {
    this.destroyChart();
  };

  destroyChart() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }
  }

  text(label) {
    return _.upperFirst(this.props.replaceTasks(label));
  }

  tooltip(xmr, point) {
    const lines = [];
    if (_.includes(ITEM_METRICS, xmr.flowMetric)) {
      const item = this.text(XMR_LABELS.tooltip_item).replace('{n}', point.x).replace('{name}', () => point.name);
      lines.push(`<strong>${escapeHTML(item)}</strong>`);
      lines.push(XMR_LABELS.tooltip_finished.replace('{date}', moment(point.date).format(DATE_FORMAT)));
    } else {
      lines.push(`<strong>${moment(point.date).format(DATE_FORMAT)}</strong>`);
    }
    lines.push(valueText(xmr.flowMetric, point.y));
    if (point.signal) {
      lines.push(`<span class="xmr-tooltip-signal">${XMR_LABELS.signal}: ${ruleText(xmr.signalRule)}</span>`);
    }
    return lines.join('<br/>');
  }

  chartConfig() {
    const { xmr } = this.props;
    const view = this;
    const byItem = _.includes(ITEM_METRICS, xmr.flowMetric);
    const half = byItem ? 0.5 : HALF_DAY;
    const points = _.map(xmr.points, (point, i) => ({ ...point, x: byItem ? i + 1 : point.date, y: point.value }));
    const earlierPoints = _.slice(points, 0, xmr.periodStart);
    const periodPoints = _.slice(points, xmr.periodStart);
    const fromX = _.first(periodPoints).x;
    const toX = _.last(periodPoints).x;

    const lineLabel = digits => ({
      enabled: true,
      formatter() {
        return this.y.toLocaleString(undefined, digits);
      },
      align: 'left',
      verticalAlign: 'middle',
      x: 6,
      crop: false,
      overflow: 'allow',
      style: { color: XMR_CHART_COLORS.line, fontWeight: '600', textOutline: 'none' }
    });
    const limitLabel = lineLabel({ maximumFractionDigits: 1 });
    const line = (name, y, options, label = { enabled: false }) => ({
      type: 'line',
      name,
      data: [{ x: fromX, y }, { x: toX, y, dataLabels: label }],
      color: XMR_CHART_COLORS.line,
      lineWidth: 1,
      marker: { enabled: false },
      enableMouseTracking: false,
      zIndex: 2,
      ...options
    });
    const pointsSeries = (name, data, color) => ({ type: 'line', name, data, color, marker: { enabled: true, radius: 3, symbol: 'circle' }, zIndex: 3 });

    const series = [];
    if (earlierPoints.length) {
      series.push(pointsSeries(XMR_LABELS.before_period, earlierPoints, XMR_CHART_COLORS.earlier));
    }
    series.push(pointsSeries(XMR_LABELS.period, periodPoints, XMR_CHART_COLORS.points));
    if (xmr.signalRule) {
      series.push({
        type: 'scatter',
        name: XMR_LABELS.signal,
        data: _.filter(points, 'signal'),
        color: XMR_CHART_COLORS.signal,
        marker: { radius: 6, symbol: 'circle', lineWidth: 1 },
        stickyTracking: false,
        zIndex: 5
      });
    }
    series.push(line(XMR_LABELS.upper_limit, xmr.upperLimit, LIMIT_LINE, limitLabel));
    series.push(line(XMR_LABELS.average, xmr.average, {}, lineLabel({ minimumFractionDigits: 1, maximumFractionDigits: 1 })));
    if (xmr.lowerLimit > 0) {
      series.push(line(XMR_LABELS.lower_limit, xmr.lowerLimit, LIMIT_LINE, limitLabel));
    }
    _.filter(xmr.sigmaLines, y => y > xmr.lowerLimit && y < xmr.upperLimit).forEach((y, i) => {
      series.push(line(XMR_LABELS.sigma_lines, y, { color: XMR_CHART_COLORS.sigma, dashStyle: 'Dot', zIndex: 1, linkedTo: i ? ':previous' : undefined }));
    });

    const plotLines = earlierPoints.length ? [{
      value: fromX - half,
      color: XMR_CHART_COLORS.line,
      dashStyle: 'Dash',
      width: 1,
      zIndex: 4,
      label: { text: XMR_LABELS.recalculated, rotation: 0, align: 'left', x: 4, y: 14, style: { color: XMR_CHART_COLORS.line, fontSize: '12px' } }
    }] : [];

    return {
      chart: { height: 380, spacingRight: 40 },
      title: { text: '' },
      credits: { enabled: false },
      exporting: { enabled: false },
      tooltip: {
        useHTML: true,
        shadow: false,
        hideDelay: 0,
        borderRadius: 5,
        borderWidth: 0,
        formatter() {
          return view.tooltip(xmr, this.point);
        }
      },
      plotOptions: { series: { animation: false, states: { inactive: { opacity: 1 } } } },
      xAxis: {
        type: byItem ? 'linear' : 'datetime',
        allowDecimals: false,
        tickInterval: byItem ? 1 : undefined,
        min: _.first(points).x - (byItem ? half : 0),
        max: _.last(points).x + (byItem ? half : 0),
        labels: { format: byItem ? '{value}' : '{value:%d %b}' },
        title: { text: byItem ? this.text(XMR_LABELS.x_items) : XMR_LABELS.x_dates },
        plotBands: [{ from: fromX - half, to: toX + half, color: XMR_CHART_COLORS.period, zIndex: 0 }],
        plotLines
      },
      yAxis: {
        floor: 0,
        allowDecimals: !_.includes(WHOLE_NUMBER_METRICS, xmr.flowMetric),
        title: { text: this.text(XMR_LABELS.y_axis[xmr.flowMetric]) }
      },
      series
    };
  }

  render() {
    const { show, onHide, analysisType, xmr } = this.props;
    return (
      <Modal show={show} onHide={onHide} onEntered={this.handleEntered} onExited={this.handleExited} centered size="lg" className="xmr-modal">
        <Modal.Header>
          <Modal.Title>{XMR_LABELS.title[analysisType]}</Modal.Title>
          <Button variant="close" onClick={onHide}>
            <img src={Close} alt={XMR_LABELS.close} />
          </Button>
        </Modal.Header>
        <Modal.Body>
          {xmr.step && (
            <div className="xmr-step">
              <div className="xmr-step-label">{XMR_LABELS.process_step}</div>
              <div>{xmr.step}</div>
            </div>
          )}
          <div className="xmr-chart" ref={this.container} />
        </Modal.Body>
      </Modal>
    );
  }
}

export default XmrChart;
